import React from 'react';
import DescriptionIcon from '@mui/icons-material/Description';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import { useBankDocumentsPage } from '../Hooks/useBankDocumentsPage';
import { BankDocumentsRoutes } from '../../../Router/path';
import ImportCsvDialog from '../Components/ImportCsvDialog';
import {
  isBankDocumentsQueueEnabled,
  isSupplierPrepaymentsEnabled,
} from '../Utils/bankDocumentsFeatureFlags';

const BankDocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    documents,
    isLoading,
    isError,
    errorMessage,
    importDialogOpen,
    isImportLoading,
    importSuccessMessage,
    importErrorMessage,
    handleOpenImportDialog,
    handleCloseImportDialog,
    handleImportSubmit,
  } = useBankDocumentsPage();

  return (
    <Stack spacing={3}>
      <CustomCard
        icon={<DescriptionIcon color="primary" fontSize="large" />}
        title="Dokumenty bankowe"
        subheader="Zarządzanie wyciągami bankowymi i księgowanie transakcji"
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Zaimportuj wyciąg bankowy w formacie CSV. Wybierz firmę i rachunek bankowy, a następnie
          wskaż plik CSV. Po zakończeniu importu lista dokumentów zostanie odświeżona automatycznie.
        </Typography>
        <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={handleOpenImportDialog}>
          Import CSV
        </Button>
        {isBankDocumentsQueueEnabled && (
          <Button sx={{ ml: 1 }} variant="text" onClick={() => navigate(BankDocumentsRoutes.queue)}>
            Otwórz kolejkę operacyjną
          </Button>
        )}
        {isSupplierPrepaymentsEnabled && (
          <>
            <Button
              sx={{ ml: 1 }}
              variant="text"
              onClick={() => navigate(BankDocumentsRoutes.supplierPrepayments)}
            >
              Otwórz prepayment dostawców
            </Button>
            <Button
              sx={{ ml: 1 }}
              variant="text"
              onClick={() => navigate(BankDocumentsRoutes.supplierPrepaymentApContractors)}
            >
              Kontrahenci AP
            </Button>
          </>
        )}
        <ImportCsvDialog
          open={importDialogOpen}
          isImporting={isImportLoading}
          errorMessage={importErrorMessage}
          onClose={handleCloseImportDialog}
          onSubmit={handleImportSubmit}
        />
        {importSuccessMessage && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {importSuccessMessage}
          </Alert>
        )}
      </CustomCard>

      <CustomCard
        title="Ostatnio zaimportowane dokumenty"
        icon={<DescriptionIcon color="primary" />}
      >
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && <Alert severity="error">{errorMessage}</Alert>}

        {!isLoading && !isError && documents.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Brak dokumentów bankowych do wyświetlenia.
          </Typography>
        )}

        {!isLoading && !isError && documents.length > 0 && (
          <List disablePadding>
            {documents.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem
                  disableGutters
                  secondaryAction={
                    isBankDocumentsQueueEnabled ? (
                      <Button size="small" onClick={() => navigate(BankDocumentsRoutes.queue)}>
                        Otwórz pozycje w kolejce
                      </Button>
                    ) : undefined
                  }
                >
                  <ListItemButton
                    sx={{ pr: 20 }}
                    onClick={() =>
                      navigate(
                        BankDocumentsRoutes.details.replace(':bankDocumentId', item.id.toString())
                      )
                    }
                  >
                    <ListItemText
                      primary={item.fileName}
                      secondary={`Pozycji: ${item.entriesCount ?? 0} • Import: ${new Date(item.importedAtUtc).toLocaleString('pl-PL')}`}
                    />
                  </ListItemButton>
                </ListItem>
                {index < documents.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </CustomCard>
    </Stack>
  );
};

export default BankDocumentsPage;
